import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../adminSurface/AdminSurface";
import ConfirmModal from "../common/ConfirmModal";
import {
  getDatabases,
  getWorkflowStartRules,
  createWorkflowStartRule,
  updateWorkflowStartRule,
  deleteWorkflowStartRule,
  testWorkflowStartRules,
} from "../../api/adminApi";

/**
 * Auto-start rules: the ingest-time end of the workflow engine. A rule watches documents as they are
 * archived and opens a case automatically when one matches.
 *
 * Three things this screen must say out loud, because getting them wrong is expensive:
 *  - rules fire on the per-document archival path only, never on bulk print-stream batches;
 *  - the first matching rule by priority wins, and nothing else is evaluated;
 *  - at most a handful of cases are opened per batch, and the rest are logged rather than silently dropped.
 */

// A start rule tests a DOCUMENT, so "Decision" — the workflow-only edge match type that reads a task
// outcome — is deliberately absent: at ingest no decision exists yet. The server refuses it too
// (IsKnownMatchType(..., allowDecision: false)); offering it here would only produce a rejected save.
const MATCH_TYPES = ["Keyword", "Regex", "Field", "Luhn"];

// How many cases one batch may open before the worker stops and logs what it skipped.
// Mirrors WorkflowAutoStartService.DefaultBatchBudget.
const BATCH_BUDGET = 25;

const dbId = (d) => d.id ?? d.Id ?? d.databaseId ?? d.DatabaseId;
const dbName = (d) => d.dbName ?? d.DbName ?? d.name ?? d.Name ?? String(dbId(d));

const collectFields = (db) =>
  (db?.relatedIndexes ?? db?.RelatedIndexes ?? db?.searchableNames ?? db?.SearchableNames ?? [])
    .map((f) => (typeof f === "string" ? f : f?.fieldName ?? f?.FieldName))
    .filter(Boolean);

const emptyRule = () => ({
  id: null,
  name: "",
  description: "",
  enabled: true,
  priority: 0,
  sourceDBInfoId: "",
  matchType: "Keyword",
  pattern: "",
  targetField: "",
  targetDefinitionId: "",
});

const fromRow = (r) => ({
  id: r.id ?? r.Id,
  name: r.name ?? r.Name ?? "",
  description: r.description ?? r.Description ?? "",
  enabled: r.enabled ?? r.Enabled ?? true,
  priority: r.priority ?? r.Priority ?? 0,
  sourceDBInfoId: (r.sourceDBInfoId ?? r.SourceDBInfoId) != null ? String(r.sourceDBInfoId ?? r.SourceDBInfoId) : "",
  matchType: r.matchType ?? r.MatchType ?? "Keyword",
  pattern: r.pattern ?? r.Pattern ?? "",
  targetField: r.targetField ?? r.TargetField ?? "",
  targetDefinitionId: (r.targetDefinitionId ?? r.TargetDefinitionId) != null ? String(r.targetDefinitionId ?? r.TargetDefinitionId) : "",
});

// "Key=Value" per line — the same shape the classification tester uses, so an admin who has met one
// tester already knows this one.
const parseTestFields = (text) => {
  const out = {};
  String(text || "")
    .split(/\r?\n/)
    .forEach((line) => {
      const i = line.indexOf("=");
      if (i <= 0) return;
      const k = line.slice(0, i).trim();
      if (k) out[k] = line.slice(i + 1).trim();
    });
  return out;
};

export default function WorkflowRules({ token, definitions, reloadKey, say, fail }) {
  const { t } = useTranslation();

  const [rules, setRules] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const [testFields, setTestFields] = useState("DocType=Contract");
  const [testFileName, setTestFileName] = useState("");
  const [testDbId, setTestDbId] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadRules = useCallback(async () => {
    if (!token) return;
    try {
      const rows = await getWorkflowStartRules(token);
      setRules(Array.isArray(rows) ? rows : rows?.items ?? []);
    } catch (e) {
      fail(e);
    }
  }, [token, fail]);

  useEffect(() => {
    loadRules();
  }, [loadRules, reloadKey]);

  useEffect(() => {
    if (!token) return;
    getDatabases(token)
      .then((d) => setDatabases(Array.isArray(d) ? d : d?.databases ?? d?.Databases ?? []))
      .catch(() => setDatabases([]));
  }, [token]);

  const defById = useMemo(() => {
    const m = new Map();
    definitions.forEach((d) => m.set(String(d.id ?? d.Id), d));
    return m;
  }, [definitions]);

  const activeDefinitions = useMemo(
    () => definitions.filter((d) => (d.status ?? d.Status) === "Active"),
    [definitions]
  );

  // Field names come from the scoped database, or the union across every database when the rule is org-wide.
  const targetFieldOptions = useMemo(() => {
    let names = [];
    if (form?.sourceDBInfoId) {
      const db = databases.find((d) => String(dbId(d)) === String(form.sourceDBInfoId));
      names = db ? collectFields(db) : [];
    } else {
      const seen = new Set();
      databases.forEach((d) => collectFields(d).forEach((n) => seen.add(n)));
      names = [...seen];
    }
    const opts = [
      { value: "", label: t("workflow.rules.targetFieldAny", { defaultValue: "Any value on the document" }) },
      { value: "FileName", label: t("workflow.rules.fileName", { defaultValue: "File name" }) },
    ];
    const seen = new Set(["", "filename"]);
    names.forEach((n) => {
      if (n && !seen.has(n.toLowerCase())) {
        opts.push({ value: n, label: n });
        seen.add(n.toLowerCase());
      }
    });
    if (form?.targetField && !seen.has(form.targetField.toLowerCase())) {
      opts.push({ value: form.targetField, label: form.targetField });
    }
    return opts;
  }, [databases, form?.sourceDBInfoId, form?.targetField, t]);

  const matchLabel = useCallback(
    (m) => t(`workflow.match.${String(m || "keyword").toLowerCase()}`, { defaultValue: String(m) }),
    [t]
  );

  const describe = useCallback(
    (r) => {
      const type = matchLabel(r.matchType ?? r.MatchType);
      const pattern = r.pattern ?? r.Pattern;
      const field = r.targetField ?? r.TargetField;
      const where = field ? ` · ${field}` : "";
      return pattern ? `${type} “${pattern}”${where}` : `${type}${where}`;
    },
    [matchLabel]
  );

  // ── Actions ───────────────────────────────────────────────────────────────────────────────

  const onNew = () => {
    setFormError(null);
    const next = emptyRule();
    // Sit after everything that already exists, so adding a rule can never silently pre-empt one.
    next.priority = rules.reduce((max, r) => Math.max(max, (r.priority ?? r.Priority ?? 0) + 10), 0);
    next.targetDefinitionId = activeDefinitions.length === 1 ? String(activeDefinitions[0].id ?? activeDefinitions[0].Id) : "";
    setForm(next);
  };

  const validate = (f) => {
    if (!f.name.trim()) return t("workflow.rules.needName", { defaultValue: "Give the rule a name." });
    if (!f.targetDefinitionId)
      return t("workflow.rules.needWorkflow", { defaultValue: "Choose the workflow this rule should start." });
    if (f.matchType !== "Luhn" && !f.pattern.trim())
      return t("workflow.rules.needPattern", { defaultValue: "This match type needs something to match on." });
    if (f.matchType === "Field" && !f.targetField)
      return t("workflow.rules.needField", {
        defaultValue: "“Field equals” compares one named field — choose which one.",
      });
    return null;
  };

  const buildBody = (f) => ({
    name: f.name.trim(),
    description: f.description || null,
    enabled: !!f.enabled,
    priority: Number(f.priority) || 0,
    sourceDBInfoId: f.sourceDBInfoId ? Number(f.sourceDBInfoId) : null,
    matchType: f.matchType,
    pattern: f.pattern ? f.pattern.trim() : null,
    targetField: f.targetField || null,
    targetDefinitionId: Number(f.targetDefinitionId),
  });

  const onSave = async () => {
    if (!form) return;
    const problem = validate(form);
    if (problem) {
      setFormError(problem);
      return;
    }
    setFormError(null);
    setBusy(true);
    try {
      const body = buildBody(form);
      const saved = form.id
        ? await updateWorkflowStartRule(token, form.id, body)
        : await createWorkflowStartRule(token, body);
      await loadRules();
      // Keep editing the row the server actually stored. Never fall back to a blank id — a form that lost
      // its id would silently create a duplicate rule on the next save.
      const savedId = saved?.id ?? saved?.Id;
      if (savedId) setForm(fromRow(saved));
      say(t("workflow.rules.saved", { defaultValue: "Rule saved." }));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setDeleteConfirm(false);
    if (!form?.id) return;
    setBusy(true);
    try {
      await deleteWorkflowStartRule(token, form.id);
      await loadRules();
      setForm(null);
      say(t("workflow.rules.deleted", { defaultValue: "Rule deleted." }));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await testWorkflowStartRules(token, parseTestFields(testFields), {
        fileName: testFileName || undefined,
        dbInfoId: testDbId || undefined,
      });
      setTestResult(res || { matched: false });
    } catch (e) {
      fail(e);
    } finally {
      setTesting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────────────────

  const targetDef = form?.targetDefinitionId ? defById.get(String(form.targetDefinitionId)) : null;
  const targetInactive = targetDef && (targetDef.status ?? targetDef.Status) !== "Active";

  const definitionOptions = useMemo(() => {
    const opts = activeDefinitions.map((d) => ({
      value: String(d.id ?? d.Id),
      label: `${d.name ?? d.Name} (v${d.version ?? d.Version})`,
    }));
    // A rule can outlive the version it points at. Keep a non-Active target visible instead of silently
    // re-pointing the rule at something else the moment someone opens it.
    if (form?.targetDefinitionId && !opts.some((o) => o.value === String(form.targetDefinitionId))) {
      const d = defById.get(String(form.targetDefinitionId));
      opts.unshift({
        value: String(form.targetDefinitionId),
        label: d ? `${d.name ?? d.Name} (v${d.version ?? d.Version} · ${d.status ?? d.Status})` : `#${form.targetDefinitionId}`,
      });
    }
    return opts;
  }, [activeDefinitions, form?.targetDefinitionId, defById]);

  return (
    <>
      <div className="wf-bar">
        <button className="asf-btn" onClick={onNew} disabled={busy}>
          {t("workflow.rules.new", { defaultValue: "New rule" })}
        </button>
        {form && (
          <>
            <button className="asf-cta" onClick={onSave} disabled={busy}>
              {t("workflow.action.save", { defaultValue: "Save" })}
            </button>
            {form.id && (
              <button className="wf-rule-delete" onClick={() => setDeleteConfirm(true)} disabled={busy}>
                {t("workflow.rules.delete", { defaultValue: "Delete rule" })}
              </button>
            )}
            <button className="asf-btn" onClick={() => { setForm(null); setFormError(null); }} disabled={busy}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </button>
          </>
        )}
      </div>

      <p className="wf-note">
        {t("workflow.rules.hint", {
          defaultValue:
            "Rules watch documents as they are archived and open a case automatically. Rules are read in priority order and the FIRST match wins — nothing after it is considered.",
        })}
      </p>
      <div className="wf-warn">
        {t("workflow.rules.scopeWarning", {
          defaultValue:
            "Auto-start applies to documents archived one at a time. Bulk print-stream batches (statements, bills) never auto-start a workflow. At most {{n}} cases are opened per batch; anything beyond that is skipped and written to the log.",
          n: BATCH_BUDGET,
        })}
      </div>

      <div className="wf-grid">
        <div className="wf-list">
          {rules.length === 0 && (
            <EmptyState
              icon="⚡"
              title={t("workflow.rules.emptyTitle", { defaultValue: "No auto-start rules" })}
              hint={t("workflow.rules.emptyHint", {
                defaultValue: "Without a rule, cases are only started by hand. Add one to route documents as they arrive.",
              })}
            />
          )}
          {rules.map((r) => {
            const id = r.id ?? r.Id;
            const on = r.enabled ?? r.Enabled;
            const def = defById.get(String(r.targetDefinitionId ?? r.TargetDefinitionId));
            return (
              <button
                key={id}
                className={`wf-list-item${form?.id === id ? " is-on" : ""}`}
                onClick={() => { setForm(fromRow(r)); setFormError(null); }}
              >
                <span>
                  <span className="wf-list-name">
                    {r.priority ?? r.Priority ?? 0}. {r.name ?? r.Name}
                  </span>
                  <br />
                  <span className="wf-list-sub">
                    {describe(r)}
                    <br />
                    → {def ? def.name ?? def.Name : `#${r.targetDefinitionId ?? r.TargetDefinitionId}`} ·{" "}
                    {t("workflow.rules.started", { defaultValue: "{{n}} started", n: r.startedCount ?? r.StartedCount ?? 0 })}
                  </span>
                </span>
                <span className={`wf-pill is-${on ? "active" : "archived"}`}>
                  {on
                    ? t("workflow.rules.on", { defaultValue: "On" })
                    : t("workflow.rules.off", { defaultValue: "Off" })}
                </span>
              </button>
            );
          })}
        </div>

        <div>
          {!form ? (
            <>
              <EmptyState
                icon="👈"
                title={t("workflow.rules.pickTitle", { defaultValue: "Pick a rule" })}
                hint={t("workflow.rules.pickHint", { defaultValue: "Choose one on the left, or add a new one." })}
              />
              <RuleTester
                t={t}
                databases={databases}
                fields={testFields}
                setFields={setTestFields}
                fileName={testFileName}
                setFileName={setTestFileName}
                dbIdValue={testDbId}
                setDbIdValue={setTestDbId}
                result={testResult}
                testing={testing}
                onTest={onTest}
              />
            </>
          ) : (
            <div className="wf-props">
              {formError && <div className="wf-warn">{formError}</div>}

              {activeDefinitions.length === 0 && (
                <div className="wf-warn">
                  {t("workflow.rules.noActiveDefs", {
                    defaultValue: "No workflow is live yet. Activate one on the Design tab before a rule can start it.",
                  })}
                </div>
              )}

              <label className="wf-field">
                <span>{t("workflow.rules.name", { defaultValue: "Name" })}</span>
                <input value={form.name} onChange={(e) => set("name", e.target.value)} />
              </label>

              <label className="wf-field">
                <span>{t("workflow.rules.description", { defaultValue: "What it is for (optional)" })}</span>
                <input value={form.description} onChange={(e) => set("description", e.target.value)} />
              </label>

              <label className="wf-field">
                <span>{t("workflow.rules.targetWorkflow", { defaultValue: "Start this workflow" })}</span>
                <select value={form.targetDefinitionId} onChange={(e) => set("targetDefinitionId", e.target.value)}>
                  <option value="">{t("workflow.rules.pickWorkflow", { defaultValue: "Choose a workflow…" })}</option>
                  {definitionOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>

              {targetInactive && (
                <div className="wf-warn">
                  {t("workflow.rules.inactiveTarget", {
                    defaultValue:
                      "That workflow is not live, so this rule matches but starts nothing. Activate the workflow, or point the rule at one that is live.",
                  })}
                </div>
              )}

              <label className="wf-field">
                <span>{t("workflow.rules.scope", { defaultValue: "Applies to" })}</span>
                <select value={form.sourceDBInfoId} onChange={(e) => set("sourceDBInfoId", e.target.value)}>
                  <option value="">{t("workflow.rules.scopeAll", { defaultValue: "Every database" })}</option>
                  {databases.map((d) => (
                    <option key={dbId(d)} value={String(dbId(d))}>
                      {dbName(d)}
                    </option>
                  ))}
                </select>
              </label>

              <div className="wf-field">
                <span>{t("workflow.rules.matchType", { defaultValue: "Match on" })}</span>
                <div className="wf-seg">
                  {MATCH_TYPES.map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={form.matchType === m ? "is-on" : ""}
                      onClick={() => set("matchType", m)}
                    >
                      {matchLabel(m)}
                    </button>
                  ))}
                </div>
              </div>

              {form.matchType !== "Luhn" && (
                <label className="wf-field">
                  <span>{t("workflow.rules.pattern", { defaultValue: "Value to look for" })}</span>
                  <input
                    value={form.pattern}
                    onChange={(e) => set("pattern", e.target.value)}
                    placeholder={t(`workflow.rules.patternHint_${form.matchType.toLowerCase()}`, {
                      defaultValue: "",
                    })}
                  />
                </label>
              )}

              <label className="wf-field">
                <span>{t("workflow.rules.targetField", { defaultValue: "Look at" })}</span>
                <select value={form.targetField} onChange={(e) => set("targetField", e.target.value)}>
                  {targetFieldOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="wf-field">
                <span>{t("workflow.rules.priority", { defaultValue: "Priority" })}</span>
                <input
                  type="number"
                  value={form.priority}
                  onChange={(e) => set("priority", e.target.value)}
                />
              </label>
              <p className="wf-note">
                {t("workflow.rules.priorityHint", {
                  defaultValue: "Lower numbers are checked first. The first rule that matches is the only one that runs.",
                })}
              </p>

              <label className="wf-check">
                <input type="checkbox" checked={!!form.enabled} onChange={(e) => set("enabled", e.target.checked)} />
                <span>
                  {t("workflow.rules.enabled", { defaultValue: "This rule is active" })}
                  <em>
                    {t("workflow.rules.enabledHint", {
                      defaultValue: "Turn it off to stop it routing without losing how it is set up.",
                    })}
                  </em>
                </span>
              </label>

              <RuleTester
                t={t}
                databases={databases}
                fields={testFields}
                setFields={setTestFields}
                fileName={testFileName}
                setFileName={setTestFileName}
                dbIdValue={testDbId}
                setDbIdValue={setTestDbId}
                result={testResult}
                testing={testing}
                onTest={onTest}
              />
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={deleteConfirm}
        variant="danger"
        icon="🗑"
        title={t("workflow.rules.confirmDeleteTitle", { defaultValue: "Delete this rule?" })}
        name={form?.name}
        body={t("workflow.rules.confirmDelete", { defaultValue: "Delete this rule? Cases it already started are unaffected." })}
        confirmLabel={t("workflow.rules.delete", { defaultValue: "Delete rule" })}
        onConfirm={onDelete}
        onCancel={() => setDeleteConfirm(false)}
      />
    </>
  );
}

/**
 * Dry run against the saved rule set. It answers two different questions on purpose: which rule MATCHES,
 * and whether that rule would actually START anything — a rule pointing at a draft workflow matches and
 * starts nothing, and an admin reading "matched" alone would reasonably assume routing works.
 */
function RuleTester({ t, databases, fields, setFields, fileName, setFileName, dbIdValue, setDbIdValue, result, testing, onTest }) {
  return (
    <div className="wf-tester">
      <div className="wf-tester-title">{t("workflow.rules.testTitle", { defaultValue: "Try it on a document" })}</div>
      <p className="wf-note">
        {t("workflow.rules.testHint", {
          defaultValue: "Nothing is started. This only reports which saved rule would win for these values.",
        })}
      </p>

      <label className="wf-field">
        <span>{t("workflow.rules.testFields", { defaultValue: "Field values (one per line, Name=Value)" })}</span>
        <textarea className="wf-tester-fields" rows={4} value={fields} onChange={(e) => setFields(e.target.value)} />
      </label>

      <label className="wf-field">
        <span>{t("workflow.rules.fileName", { defaultValue: "File name" })}</span>
        <input value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="contract-2026.pdf" />
      </label>

      <label className="wf-field">
        <span>{t("workflow.rules.testDb", { defaultValue: "As if archived into" })}</span>
        <select value={dbIdValue} onChange={(e) => setDbIdValue(e.target.value)}>
          <option value="">{t("workflow.rules.scopeAll", { defaultValue: "Every database" })}</option>
          {databases.map((d) => (
            <option key={dbId(d)} value={String(dbId(d))}>
              {dbName(d)}
            </option>
          ))}
        </select>
      </label>

      <button className="asf-btn" onClick={onTest} disabled={testing}>
        {t("workflow.rules.testRun", { defaultValue: "Test" })}
      </button>

      {result && !(result.matched ?? result.Matched) && (
        <div className="wf-note wf-tester-out">
          {t("workflow.rules.testNoMatch", { defaultValue: "No rule matches — this document would be archived without starting a case." })}
        </div>
      )}
      {result && (result.matched ?? result.Matched) && (
        <div className={(result.wouldStart ?? result.WouldStart) ? "wf-ok wf-tester-out" : "wf-warn wf-tester-out"}>
          {(result.wouldStart ?? result.WouldStart)
            ? t("workflow.rules.testWouldStart", {
                defaultValue: "“{{rule}}” matches and would start “{{workflow}}”.",
                rule: result.ruleName ?? result.RuleName,
                workflow: result.definitionName ?? result.DefinitionName,
              })
            : t("workflow.rules.testWouldNot", {
                defaultValue: "“{{rule}}” matches but would start nothing.",
                rule: result.ruleName ?? result.RuleName,
              })}
          {(result.reason ?? result.Reason) && <div>{result.reason ?? result.Reason}</div>}
        </div>
      )}
    </div>
  );
}
